import axios, { AxiosResponse } from 'axios';
// import { setupInterceptorsTo } from './Interceptors';
import { setupInterceptorsTo } from '@/shared/services/httpInterceptors';

import { LoginRequest, RefreshLoginRequest, LogoutRequest } from '@/modules/ewwms/models/Lager/LagerModels';

import {
  GetBestellungRequest,
  UpdateBestellungLkwKennzeichenRequest,
  SetListeartikelKommissionRequest,
  GetBestellungDokumentRequest,
} from '@/modules/ewwms/models/Lager/BestellungenModels';

import {
  Umfuhr,
  CreateUmfuhrRequest,
  Umfuhrpos,
  SetListeartikelUmfuhrRequest,
  UpdateUmfuhrLkwKennzeichenRequest,
} from '@/modules/ewwms/models/Lager/UmfuhrModels';

import { CreateApiKeyRequest } from '@/modules/ewwms/models/ApiKeyModels';

/* DEV */
import config from '@/../appsettings.dev.json';

/* PROD */
// const appsettings = await fetch('/appsettings.test.json')
// const appsettings = await fetch('/appsettings.json');
// const config = await appsettings.json();

const env = window.__ENV__ ?? null;

const BASE_URL = (env != undefined && env?.BASE_URL != undefined && env?.BASE_URL != '' ? env?.BASE_URL : config.api.baseUrl) + '/v1';
const TENANT = env != undefined && env?.AUTH_DEFAULT_REALM != undefined && env?.AUTH_DEFAULT_REALM != '' ? env?.AUTH_DEFAULT_REALM : config.auth.defaultRealm;

const apiClient = setupInterceptorsTo(
  axios.create({
    baseURL: BASE_URL,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      tenant: TENANT,
    },
  })
);

export default {
  //#region Auth
  getAzureLoginConfig(): Promise<AxiosResponse> {
    return apiClient.get('/login/azureconfig');
  },

  loginAzure(idToken: String): Promise<AxiosResponse> {
    const config = {
      headers: {
        idtoken: idToken.toString(),
      },
    };

    return apiClient.get('/login/azure', config);
  },

  login(data: LoginRequest): Promise<AxiosResponse> {
    return apiClient.post('/login', data);
  },

  refreshLogin(data: RefreshLoginRequest): Promise<AxiosResponse> {
    return apiClient.post('/login/refresh', data);
  },

  logout(data: LogoutRequest): Promise<AxiosResponse> {
    return apiClient.post('/logout', data);
  },
  //#endregion

  //#region  ApiKeys
  getAllApiKeys(): Promise<AxiosResponse> {
    return apiClient.get('/apikeys');
  },

  createApiKey(data: CreateApiKeyRequest): Promise<AxiosResponse> {
    return apiClient.post('/apikey', data);
  },

  deleteApiKey(id: number): Promise<AxiosResponse> {
    return apiClient.delete(`/apikey/${id}`);
  },
  //#endregion

  //#region Database
  isDatabaseUpToDate(): Promise<AxiosResponse> {
    return apiClient.get('/database/status');
  },

  updateDatabase(): Promise<AxiosResponse> {
    return apiClient.get('/database/update');
  },
  //#endregion

  //#region Listeartikel
  getArtikel(): Promise<AxiosResponse> {
    return apiClient.get('/lager/artikel');
  },
  //#endregion

  //#region Bestellungen
  getBestellungen(data: GetBestellungRequest): Promise<AxiosResponse> {
    return apiClient.get('/bestellungen?vonlagernr=' + data.vonlagernr + '&date=' + data.datum.toISOString());
  },

  updateLkwKennzeichen(data: UpdateBestellungLkwKennzeichenRequest): Promise<AxiosResponse> {
    return apiClient.put('/bestellung/lkwkennzeichen', data);
  },

  setBestellungAbgeschlossen(data: number): Promise<AxiosResponse> {
    return apiClient.put(`/bestellung/verladen/${data}`);
  },

  getBestellungDokument(data: GetBestellungDokumentRequest): Promise<AxiosResponse> {
    return apiClient.get(`/bestellung/dokument?docbarcode=${data.docbarcode}&docartid=${data.docartid}`, { responseType: 'blob' }); // Important to specify responseType as blob
  },

  updateBestellungDokumente(data: number): Promise<AxiosResponse> {
    return apiClient.put(`/bestellung/dokumente/${data}`);
  },

  //#endregion

  //#region Listeartikel
  getLagerortBestand(lagerortid: string): Promise<AxiosResponse> {
    return apiClient.get(`/lager/listeartikel/bestand?lagerortid=${lagerortid}`);
  },

  getBestellungPaletten(data: number): Promise<AxiosResponse> {
    return apiClient.get(`/lager/listeartikel/kommission/${data}`);
  },

  getUmfuhrPaletten(data: number): Promise<AxiosResponse> {
    return apiClient.get(`/lager/listeartikel/umfuhr/${data}`);
  },

  setListeartikelKommission(data: SetListeartikelKommissionRequest): Promise<AxiosResponse> {
    return apiClient.put('/lager/listeartikel/kommission', data);
  },

  setListeartikelUmfuhr(data: SetListeartikelUmfuhrRequest): Promise<AxiosResponse> {
    return apiClient.put('/lager/listeartikel/umfuhr', data);
  },

  setListeartikelUmfuhrEntladen(data: SetListeartikelUmfuhrRequest): Promise<AxiosResponse> {
    return apiClient.put('/lager/listeartikel/umfuhr/entladen', data);
  },
  //#endregion

  //#region Umfuhr
  getAllUmfuhren(data: Date | null): Promise<AxiosResponse> {
    var endpoint = '/umfuhren';

    if (data != null) {
      endpoint += `?date=${data.toISOString()}`;
    }

    return apiClient.get(endpoint);
  },

  createAuftrag(data: CreateUmfuhrRequest) {
    return apiClient.post('/umfuhr', data);
  },

  updateAuftrag(data: Umfuhr) {
    return apiClient.put('/umfuhr', data);
  },

  updateUmfuhrLkwKennzeichen(data: UpdateUmfuhrLkwKennzeichenRequest): Promise<AxiosResponse> {
    return apiClient.put('/umfuhr/lkwkennzeichen', data);
  },

  setUmfuhrVerladen(data: number): Promise<AxiosResponse> {
    return apiClient.put(`/umfuhr/verladen/${data}`);
  },

  setUmfuhrEntladen(data: number): Promise<AxiosResponse> {
    return apiClient.put(`/umfuhr/entladen/${data}`);
  },

  deleteAuftrag(id: number) {
    return apiClient.delete(`/umfuhr/${id}`);
  },
  //#endregion

  //#region Umfuhrpos
  getAllUmfuhrpos(): Promise<AxiosResponse> {
    return apiClient.get('/umfuhrpos');
  },

  getAllUmfuhrposByUmfuhrid(umfuhrid: number): Promise<AxiosResponse> {
    return apiClient.get(`/umfuhrpos/umfuhr/${umfuhrid}`);
  },

  createUmfuhrpos(data: Umfuhrpos) {
    return apiClient.post('/umfuhrpos', data);
  },

  createUmfuhrposRange(data: Array<Umfuhrpos>) {
    return apiClient.post('/umfuhrpos/range', data);
  },

  updateUmfuhrpos(data: Umfuhrpos) {
    return apiClient.put('/umfuhrpos', data);
  },

  deleteUmfuhrpos(id: number) {
    return apiClient.delete(`/umfuhrpos/${id}`);
  },
  //#endregion
};
